import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Loader2, CheckCircle2, ArrowLeft, TrendingUp,
  ArrowDown, ArrowUp, Minus, Info,
} from "lucide-react";
import api from "@/lib/api";

const METRIC_LABEL = {
  lead_to_contact:            "Lead → Contactado",
  contact_to_qualified:       "Contactado → Qualificado",
  qualified_to_estimate:      "Qualificado → Estimativa",
  estimate_to_rt:             "Estimativa → RT",
  rt_scheduled_to_realised:   "RT Agendada → Realizada",
  rt_to_proposal:             "RT → Proposta",
  proposal_to_deal:           "Proposta → Negócio",
  lead_to_deal:               "Lead → Negócio (global)",
  sla_compliance:             "SLA 15min",
};

const LEVEL_KEYS = ["critical", "minimum", "target", "excellent"];
const LEVEL_META = {
  critical:  { label: "Crítico",   color: "text-[hsl(var(--danger))]",    bg: "bg-[hsl(var(--danger-bg))]" },
  minimum:   { label: "Mínimo",    color: "text-[hsl(var(--warning))]",   bg: "bg-[hsl(var(--warning-bg))]" },
  target:    { label: "Meta",      color: "text-[hsl(var(--success))]", bg: "bg-[hsl(var(--success-bg))]" },
  excellent: { label: "Excelente", color: "text-[hsl(var(--info))]",    bg: "bg-[hsl(var(--info-bg))]" },
};

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function DeltaBadge({ current, suggested }) {
  if (current == null || suggested == null) return null;
  const diff = suggested - current;
  const rounded = Math.round(diff * 10) / 10;
  const abs = Math.abs(rounded);
  if (abs < 0.5) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
        <Minus className="w-3 h-3" /> igual
      </span>
    );
  }
  const up = rounded > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${up ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {up ? "+" : ""}{rounded}
    </span>
  );
}

export default function BaselinesSettings() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState({}); // metric_key -> bool

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/baselines");
      setData(data);
      // Por default pré-selecciona todas as métricas que têm sugestão
      const initSel = {};
      Object.keys(data.suggestions || {}).forEach((k) => { initSel[k] = true; });
      setSelected(initSel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar baselines.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/baselines/generate", { lookback_days: days });
      if (data.status === "insufficient_data") {
        toast.warning("Dados insuficientes — precisa de pelo menos 7 dias com volume.");
      } else {
        toast.success(`Sugestões recalculadas com base nos últimos ${days} dias.`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar sugestões.");
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    const keys = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!keys.length) {
      toast.error("Escolhe pelo menos uma métrica para aplicar.");
      return;
    }
    if (!window.confirm(`Aplicar ${keys.length} sugestão(ões) ao config actual de KPI thresholds?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/baselines/apply", { keys });
      if (data.ok) {
        toast.success(`Aplicadas ${data.applied_keys.length} sugestão(ões).`);
        await load();
      } else {
        toast.error(data.reason || "Sem sugestões para aplicar.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao aplicar sugestões.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto py-24 text-center text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3" /> A carregar…
      </div>
    );
  }

  const suggestions = data?.suggestions || {};
  const current = data?.current_thresholds || {};
  const stats = data?.stats || {};
  const metricKeys = Object.keys(METRIC_LABEL);
  const suggestedCount = Object.keys(suggestions).length;
  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="max-w-6xl mx-auto py-8 px-4" data-testid="baselines-page">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Voltar
      </Link>

      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-foreground font-semibold">Fase 5c</div>
          <h1 className="text-3xl font-semibold text-foreground mt-1">Baselines automáticos</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            O sistema recalcula sugestões de KPI thresholds diariamente às 04:00 (Europe/Lisbon)
            com base nos últimos 90 dias. As sugestões ficam <b>pendentes</b> até seres tu a aprovar.
            Método: <span className="font-mono text-[11px] bg-muted px-1.5 py-0.5 rounded">crítico=p25 · mínimo=p40 · meta=p60 · excelente=p90</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="text-xs text-muted-foreground">
            Janela (dias)
            <input
              type="number" min={30} max={180} step={30} value={days}
              onChange={(e) => setDays(parseInt(e.target.value) || 90)}
              className="ml-2 w-20 border border-border rounded-lg px-2 py-1 text-sm"
              data-testid="baselines-days-input"
            />
          </label>
          <button
            onClick={generate}
            disabled={busy}
            className="inline-flex items-center gap-2 bg-foreground text-background text-sm px-4 py-2 rounded-lg hover:bg-[#1E293B] disabled:opacity-50"
            data-testid="baselines-generate-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Recalcular sugestões
          </button>
        </div>
      </div>

      {/* Meta box */}
      <div className="bg-card border border-border rounded-2xl p-6 mb-6 flex flex-wrap items-center gap-6 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Última geração</div>
          <div className="text-foreground font-medium mt-1">{fmtDT(data?.generated_at)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Estado</div>
          <div className="mt-1">
            {data?.status === "pending" && <span className="text-[hsl(var(--warning))] font-medium">🟡 Pendentes ({suggestedCount})</span>}
            {data?.status === "applied" && <span className="text-[hsl(var(--success))] font-medium">🟢 Aplicadas ({suggestedCount})</span>}
            {data?.status === "insufficient_data" && <span className="text-[hsl(var(--danger))] font-medium">🔴 Dados insuficientes</span>}
            {data?.status === "not_generated" && <span className="text-muted-foreground">Nunca gerado</span>}
          </div>
        </div>
        {data?.last_applied_at && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Última aplicação</div>
            <div className="text-foreground mt-1">
              {fmtDT(data.last_applied_at)}
              {data.last_applied_by ? <span className="text-muted-foreground ml-2">por {data.last_applied_by}</span> : null}
            </div>
          </div>
        )}
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground">{selCount} de {suggestedCount} seleccionadas</span>
          <button
            onClick={apply}
            disabled={busy || !suggestedCount || !selCount}
            className="inline-flex items-center gap-2 bg-foreground text-background text-sm px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-40"
            data-testid="baselines-apply-btn"
          >
            <CheckCircle2 className="w-4 h-4" /> Aplicar seleccionadas
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-muted-foreground text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 w-8"></th>
              <th className="text-left px-4 py-3">Métrica</th>
              {LEVEL_KEYS.map((lv) => (
                <th key={lv} className="text-right px-4 py-3">
                  {LEVEL_META[lv].label}
                </th>
              ))}
              <th className="text-right px-4 py-3">Amostra</th>
            </tr>
          </thead>
          <tbody>
            {metricKeys.map((key) => {
              const cur = current[key] || {};
              const sug = suggestions[key];
              const stat = stats[key] || {};
              return (
                <tr key={key} className="border-t border-border" data-testid={`baseline-row-${key}`}>
                  <td className="px-4 py-3 align-top">
                    {sug && (
                      <input
                        type="checkbox"
                        checked={!!selected[key]}
                        onChange={(e) => setSelected((s) => ({ ...s, [key]: e.target.checked }))}
                        className="w-4 h-4 accent-[#1D4ED8]"
                        data-testid={`baseline-toggle-${key}`}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="font-semibold text-foreground">{METRIC_LABEL[key]}</div>
                    {stat.avg != null && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        média {stat.avg}% · min {stat.min} · max {stat.max}
                      </div>
                    )}
                  </td>
                  {LEVEL_KEYS.map((lv) => {
                    const curV = cur[lv];
                    const sugV = sug ? sug[lv] : null;
                    return (
                      <td key={lv} className={`px-4 py-3 text-right align-top ${LEVEL_META[lv].bg}`}>
                        <div className={`text-sm font-semibold ${LEVEL_META[lv].color}`}>
                          {sugV != null ? sugV : "—"}
                          {sugV != null && curV != null && (
                            <span className="ml-2">
                              <DeltaBadge current={curV} suggested={sugV} />
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          actual: {curV != null ? curV : "—"}
                        </div>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-right align-top">
                    <div className="text-xs text-foreground">{stat.sample_days || 0}d</div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex items-start gap-3 text-xs text-muted-foreground bg-muted/40 border border-border rounded-xl p-4">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-medium text-foreground mb-1">Como funciona</div>
          <div>
            Cada dia útil na janela seleccionada é medido individualmente. Requerimos volume mínimo por dia:
            SLA precisa de ≥3 leads recebidos, cada conversão precisa de ≥3 leads no denominador. Métricas
            com &lt;7 dias válidos não têm sugestão (fica evidenciado a cinzento). As alterações vão para o config em
            <code className="font-mono bg-card px-1 py-0.5 rounded ml-1 border border-border">kpi_thresholds_config</code>
            após clicares “Aplicar seleccionadas”.
          </div>
        </div>
      </div>
    </div>
  );
}
